<template>
  <v-card data-testid="regimen_grid_schedule_edit">
    <v-card-text>
      <v-alert v-if="!localValue.grid_schedule" type="error" outlined dense>
        {{ $t("regimen_container_edit.snapshot_missing") }}
      </v-alert>
      <template v-else>
        <v-layout row wrap>
          <v-flex xs12 md8 class="pr-md-6">
            <h3 class="mb-3">{{ localValue.grid_schedule.title }}</h3>
            <p v-if="localValue.grid_schedule.description">{{ localValue.grid_schedule.description }}</p>
            <v-select
              v-model="localValue.grid_current_stage"
              outlined
              :label="$t('regimen_edit.choose_schedule_stage')"
              :items="grid_current_stage_options"
              :rules="[(value) => stage_exists(value) || $t('regimen_container_edit.starting_stage_required')]"
            >
              <template v-slot:item="data">
                <v-list-item-content>
                  <v-list-item-title v-html="data.item.text" />
                  <v-list-item-subtitle v-html="data.item.description" />
                </v-list-item-content>
              </template>
            </v-select>
          </v-flex>
          <v-flex xs12 md4>
            <RegimenGridCustomizationPanel
              :status="customization_status"
              :can-update-from-source="can_update_from_source"
              @customize="open_grid_schedule_edit_dialog"
              @update-from-source="update_from_source"
            />
          </v-flex>
        </v-layout>

        <v-sheet outlined rounded class="mt-6">
          <v-date-picker
            v-model="schedule_anchor_date"
            class="opticalgym_grid_anchor_picker"
            :events="grid_schedule_calendar_events"
            full-width
            @mouseenter:date="grid_date_mouseover"
            @mouseleave:date="grid_date_mouseover_text = ''"
          />
          <div style="height: 20px; padding-left: 40px; overflow: hidden; position: relative; bottom: 20px">
            {{ grid_date_mouseover_text }}
          </div>
        </v-sheet>
      </template>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn
        color="primary"
        :disabled="!stage_exists(localValue.grid_current_stage)"
        data-testid="regimen_grid_schedule_edit_save"
        @click="save"
      >
        {{ $t("common.save") }}
      </v-btn>
    </v-card-actions>

    <v-dialog v-model="grid_schedule_edit_dialog" max-width="1200" :fullscreen="small_screen || phone_screen">
      <GridScheduleEdit
        v-if="grid_schedule_edit_dialog"
        ref="grid_schedule_edit"
        :existing_schedule="localValue.grid_schedule"
        :clinic_id="clinic_id"
        :week="grid_current_stage_week"
        @close="close_grid_schedule_edit_dialog"
        @grid-schedule-updated="grid_schedule_updated"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import Vue from "vue";
import { mapState } from "vuex";
import GridScheduleEdit from "./GridScheduleEdit.vue";
import RegimenGridCustomizationPanel from "./RegimenGridCustomizationPanel.vue";
import {
  cloneSourceOntoAssignment,
  ensureValidGridScheduleStage,
  gridCustomizationStatus,
  resolveSourceGrid,
} from "../lib/regimen_grid_customization";

export default {
  components: { GridScheduleEdit, RegimenGridCustomizationPanel },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    let schedule_anchor_date = null;
    const stage = this.value.grid_schedule
      ? this.value.grid_schedule.grid.find(
        (candidate) => candidate.id === this.value.grid_current_stage,
      )
      : null;
    if (this.value.grid_next_stage_date && stage) {
      const anchor_date = new Date(Date.parse(this.value.grid_next_stage_date));
      const interval_ms = stage.interval * 24 * 60 * 60 * 1000;
      schedule_anchor_date = new Date(anchor_date.getTime() - interval_ms).toISOString().substr(0, 10);
    }

    return {
      schedule_anchor_date,
      grid_date_mouseover_text: "",
      grid_schedule_edit_dialog: false,
    };
  },
  computed: {
    ...mapState(["current_clinic_id", "grid_schedules", "small_screen", "phone_screen"]),
    localValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    clinic_id() {
      return (this.localValue.grid_schedule && this.localValue.grid_schedule.clinic_id)
        || this.current_clinic_id
        || "";
    },
    source_grid() {
      return resolveSourceGrid(this.localValue, { grid_schedules: this.grid_schedules });
    },
    customization_status() {
      return gridCustomizationStatus(this.localValue, this.source_grid);
    },
    can_update_from_source() {
      return Boolean(
        this.source_grid
        && (this.customization_status === "customized" || this.customization_status === "out_of_date"),
      );
    },
    current_stage() {
      if (!this.localValue.grid_schedule) return null;
      return this.localValue.grid_schedule.grid.find(
        (stage) => stage.id === this.localValue.grid_current_stage,
      ) || null;
    },
    grid_current_stage_options() {
      const options = [];
      if (this.localValue.grid_schedule) {
        for (const i in this.localValue.grid_schedule.grid) {
          options.push({
            text: "Week " + (parseInt(i) + 1),
            value: this.localValue.grid_schedule.grid[i].id,
          });
        }
      }
      return options;
    },
    grid_current_stage_week() {
      if (!this.localValue.grid_current_stage || !this.localValue.grid_schedule) {
        return 1;
      }
      for (const i in this.localValue.grid_schedule.grid) {
        if (this.localValue.grid_schedule.grid[i].id == this.localValue.grid_current_stage) {
          return parseInt(i) + 1;
        }
      }
      return 1;
    },
    next_stage_date() {
      if (!this.schedule_anchor_date || !this.current_stage) {
        return null;
      }
      const anchor_date = new Date(Date.parse(this.schedule_anchor_date));
      const interval = parseInt(this.current_stage.interval);
      const next_date = new Date(anchor_date.getTime() + interval * 24 * 60 * 60 * 1000);
      return next_date.toISOString().substr(0, 10);
    },
    grid_schedule_events() {
      const events = [];
      if (!this.localValue.grid_schedule) {
        return events;
      }

      let anchor_date;
      const sched_stage = this.current_stage;
      if (this.schedule_anchor_date) {
        anchor_date = new Date(Date.parse(this.schedule_anchor_date));
      } else if (this.localValue.grid_next_stage_date && sched_stage) {
        const anchor_date_ms = Date.parse(this.localValue.grid_next_stage_date);
        const interval_ms = sched_stage.interval * 24 * 60 * 60 * 1000;
        anchor_date = new Date(anchor_date_ms - interval_ms);
      } else {
        anchor_date = new Date();
      }

      let skipping = true;
      for (const i in this.localValue.grid_schedule.grid) {
        const grid_stage = this.localValue.grid_schedule.grid[i];
        if (grid_stage.id == this.localValue.grid_current_stage) {
          skipping = false;
        }
        if (skipping) {
          continue;
        }
        anchor_date.setDate(anchor_date.getDate() + grid_stage.interval);
        events.push({
          date: anchor_date.toISOString().substr(0, 10),
          title: "Week " + (parseInt(i) + 2),
        });
      }

      return events;
    },
    grid_schedule_calendar_events() {
      const calendar_events = {};
      for (const i in this.grid_schedule_events) {
        const event = this.grid_schedule_events[i];
        calendar_events[event.date] = i == this.grid_schedule_events.length - 1 ? "red" : "blue";
      }
      return calendar_events;
    },
  },
  methods: {
    stage_exists(stageId) {
      return this.grid_current_stage_options.some((option) => option.value === stageId);
    },
    grid_date_mouseover(date) {
      const now = new Date();
      const today = now.toISOString().substr(0, 10);
      if (date < today) {
        return "";
      }

      let first_week_id = "";
      for (const option of this.grid_current_stage_options) {
        if (option.value == this.localValue.grid_current_stage) {
          first_week_id = option.text;
          break;
        }
      }

      this.grid_date_mouseover_text = first_week_id;
      for (let i = 0; i < this.grid_schedule_events.length; i++) {
        const event = this.grid_schedule_events[i];
        if (date >= event.date) {
          this.grid_date_mouseover_text = event.title;
          if (i == this.grid_schedule_events.length - 1) {
            this.grid_date_mouseover_text = "";
          }
        }
      }
    },
    open_grid_schedule_edit_dialog() {
      this.grid_schedule_edit_dialog = true;
      Vue.nextTick(() => {
        this.$refs.grid_schedule_edit.reset();
        this.$refs.grid_schedule_edit.load();
      });
    },
    close_grid_schedule_edit_dialog() {
      this.grid_schedule_edit_dialog = false;
    },
    grid_schedule_updated(updated_schedule) {
      const previousStage = this.localValue.grid_current_stage;
      Vue.set(this.localValue, "grid_schedule", updated_schedule);
      Vue.set(this.localValue, "grid_customized", true);
      ensureValidGridScheduleStage(this.localValue);
      if (this.localValue.grid_current_stage !== previousStage) {
        this.schedule_anchor_date = null;
      }
      this.close_grid_schedule_edit_dialog();
    },
    update_from_source() {
      if (!this.source_grid) return;
      const previousStage = this.localValue.grid_current_stage;
      cloneSourceOntoAssignment(this.localValue, this.source_grid);
      Vue.set(this.localValue, "grid_schedule", this.localValue.grid_schedule);
      Vue.set(this.localValue, "grid_customized", false);
      if (this.localValue.grid_current_stage !== previousStage) {
        this.schedule_anchor_date = null;
      }
    },
    save() {
      ensureValidGridScheduleStage(this.localValue);
      if (!this.stage_exists(this.localValue.grid_current_stage)) return;
      this.$set(this.localValue, "grid_next_stage_date", this.next_stage_date);
      this.$emit("save");
    },
  },
};
</script>

<style>
.opticalgym_grid_anchor_picker .v-picker__title {
  display: none;
}
</style>
